import { useCallback, createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Encrypt } from '../utils/encryption';
import { extendToken } from '../config/api';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setTokenState] = useState(null);
  const refreshingRef = useRef(false);
  const navigate = useNavigate();

  const setToken = (initialToken) => {
    const decoded = decodeToken(initialToken);
    if (!decoded?.exp || decoded.exp < Date.now() / 1000) {
      console.warn('❌ Tried to set expired token');
      setTokenState(null);
    } else {
      setTokenState(initialToken);
    }
  };

  const decodeToken = (token) => {
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  };

  const logout = useCallback(() => {
    setTokenState(null);
    navigate('/login', {replace: true});
  }, [navigate]);


  useEffect(() => {
    let intervalId;

    if (token) {
      const isExpiringSoon = (token, withinSeconds = 606) => {
        const decoded = decodeToken(token);
        if (!decoded?.exp) return true;

        const currentTime = Date.now() / 1000;
        return decoded.exp - currentTime < withinSeconds;
      };

      const refreshToken = async () => {
        if (refreshingRef.current) return;
        refreshingRef.current = true;

        try {
          const response = await axios.post(extendToken, Encrypt(token), {
            headers: {
              'Content-Type': 'application/json',
            },
          });

          const newToken = response.data?.token;
          if (newToken) {
            console.log('🔁 Token refreshed');
            setTokenState(newToken);
          } else {
            console.warn('⚠️ No valid token available');
            logout();
          }
        } catch (error) {
          console.error('🔒 Failed to refresh token:', error);
          logout();
        } finally {
          refreshingRef.current = false;
        }
      };

      const checkAndRefresh = () => {
        if (isExpiringSoon(token)) {
          refreshToken();
        }
      };

      checkAndRefresh(); // ⏱️ Immediate check
      intervalId = setInterval(checkAndRefresh, 1500 * 1000); // ⏳ Every 5 minutes
    }

    return () => clearInterval(intervalId);
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ token, setToken, logout, decodeToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
